import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Button, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography, TextField,
  MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  PaymentRequisition,
  useGetPaymentRequisitionsQuery,
  useGetPurchaseOrdersQuery,
  usePatchPaymentRequisitionsByIdMutation,
  usePostPaymentRequisitionsMutation
} from '../../../redux/vmsApi';

import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import NumbersIcon from '@mui/icons-material/Numbers';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';

import { toast } from 'react-toastify';
import { formatCurrency } from '../../../utils/currencyUtil';
import { useAppSelector } from '../../../redux/hooks';
import RenderApproverName from "./RenderApproverName";
import { getCurrentDate } from 'utils/dateUtil';

interface PaymentRequestProps {
  procurementProcess: any
}


const PaymentRequest: React.FC<PaymentRequestProps> = ({ procurementProcess }) => {
  const user = useAppSelector(state => state.auth.user);
  const isFundsRequest = procurementProcess?.requestType === 'FUNDS REQUEST';
  const requisitionNumber = procurementProcess?.requisition?.reqNumber;
  const requisitionTotalCost = procurementProcess?.requisition?.totalCost || 0;
  const projectId = procurementProcess?.requisition?.project?.id;

  const { data: purchaseOrders, isLoading: poLoading, isError: poError } =
    useGetPurchaseOrdersQuery(
      { procurementProcessId: procurementProcess.id },
      { skip: isFundsRequest }
    );
  const { data: paymentReqs, isLoading: prLoading, isError: prError, refetch } =
    useGetPaymentRequisitionsQuery(
      isFundsRequest
        ? { reqNumber: requisitionNumber }
        : { purchaseOrderNumber: purchaseOrders?.[0]?.purchaseOrderNumber },
      { skip: isFundsRequest ? !requisitionNumber : !purchaseOrders?.[0]?.purchaseOrderNumber }
    );
  const [createPaymentReq] = usePostPaymentRequisitionsMutation();
  const [updatePaymentReq] = usePatchPaymentRequisitionsByIdMutation();



  const [totalRequested, setTotalRequested] = useState(0);


  const [currentPaymentReq, setCurrentPaymentReq] = useState<PaymentRequisition | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<PaymentRequisition>>({
    paymentMethod: '',
    amount: 0,
    rate: 1,
    dueDate: getCurrentDate(),
    status: 'Awaiting Approval',
  });

  const activePaymentReqs = useMemo(
    () => (paymentReqs ?? []).filter(
      (req: any) => String(req?.status ?? '').toLowerCase() !== 'cancelled',
    ),
    [paymentReqs],
  );

  useEffect(() => {
    if (!paymentReqs || prLoading || prError) {
      return;
    }

    const requestedAmount = activePaymentReqs.reduce(
      (sum: number, req: any) => sum + (req.amount || 0),
      0
    );

    setTotalRequested(requestedAmount);

    if (isFundsRequest) {
      const remainingAmount = requisitionTotalCost - requestedAmount;

      setFormData((prev: any) => ({
        ...prev,
        title: `${procurementProcess?.requisition?.title || ''}`,
        currency: prev?.currency || 'usd',
        refDate: new Date().toISOString(),
        dueDate: getCurrentDate(),
        rate: 1,
        amount: remainingAmount > 0 ? remainingAmount : 0
      }));
      return;
    }

    if (purchaseOrders?.[0]) {
      const po = purchaseOrders[0];
      const remainingAmount = (po.quotation?.grandTotal || 0) - requestedAmount;

      setFormData((prev: any) => ({
        ...prev,
        title: `${procurementProcess.requisition.title}`,
        currency: po.quotation?.currency || 'usd',
        refDate: new Date().toISOString(),
        dueDate: getCurrentDate(),
        rate: 1,
        purchaseOrder: po,
        amount: remainingAmount > 0 ? remainingAmount : 0,
        paymentMethod: remainingAmount > 100 ? 'Nostro' : (prev.paymentMethod || 'Cash')
      }));
    }
  }, [
    isFundsRequest,
    purchaseOrders,
    procurementProcess,
    paymentReqs,
    prLoading,
    prError,
    requisitionTotalCost
  ]);

  useEffect(() => {
    if (paymentReqs) {
      const total = activePaymentReqs.reduce((sum, req) => sum + (req.amount || 0), 0);
      setTotalRequested(total);
    }
  }, [paymentReqs, activePaymentReqs]);

  const handleEditClick = (paymentReq: PaymentRequisition) => {
    setCurrentPaymentReq(paymentReq);
    setFormData({
      ...paymentReq, status: 'Awaiting Approval'
    });
    setIsDialogOpen(true);
  };

  const handleCreateClick = () => {
    setCurrentPaymentReq(null);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setCurrentPaymentReq(null);
    setFormData({
      paymentMethod: '',
      amount: 0,
      rate: 1,
      dueDate: getCurrentDate(),
      dateCreated: '',
      status: 'Awaiting Approval',
      paymentType: 'DEPOSIT'
    });
  };

  const handleFormChange = (field: keyof PaymentRequisition, value: any) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'amount' && !prev.paymentMethod && Number(value) > 100) next.paymentMethod = 'Nostro';
      return next;
    });
  };

  const hasValidAmount =
    typeof formData.amount === 'number' &&
    !Number.isNaN(formData.amount) &&
    formData.amount >= 0;

  const handleSubmit = async () => {
    if (formData?.dueDate && formData?.dateCreated && formData?.paymentMethod && formData.paymentType && hasValidAmount)
      try {


        const commonData = {
          paymentMethod: formData.paymentMethod,
          paymentType: formData.paymentType,
          rate: formData.rate,
          currency: formData.currency,
          amount: formData.amount,
          dueDate: new Date(formData?.dueDate).toISOString(),
          dateCreated: new Date(formData?.dateCreated).toISOString(),
        };

        if (currentPaymentReq && currentPaymentReq.id) {

          await updatePaymentReq({
            id: currentPaymentReq.id,
            paymentRequisitionPartial: {
              id: currentPaymentReq.id,
              ...commonData,
              ...(formData.status && { status: formData.status }),
              ...(formData.currency && { currency: formData.currency }),
            }
          }).unwrap();
        } else {
          if (user?.department) {
            const newPaymentRequisition: any = {
              ...commonData,
              currency: formData.currency || purchaseOrders?.[0]?.quotation?.currency || 'usd',
              department: user.department,
              development: procurementProcess.requisition.development.name,
              project: procurementProcess.requisition.project.name,
              preparedBy: user,
              dueDate: new Date(formData?.dueDate).toISOString(),
              dateCreated: new Date(formData?.dateCreated).toISOString(),
              lastActionedDate: new Date().toISOString(),
              status: "Awaiting Approval",
            };

            if (purchaseOrders?.[0]) {
              newPaymentRequisition.purchaseOrder = purchaseOrders[0];
            } else if (isFundsRequest) {
              newPaymentRequisition.procurementProcessId = procurementProcess.id;
            } else {
              throw new Error('Purchase Order is required to create a payment requisition.');
            }

            await createPaymentReq({
              newPaymentRequisition
            }).unwrap();
          } else {
            // toast.error("You are not assigned to a department", {
            //   position: toast.POSITION.TOP_RIGHT,
            // });
          }
        }

        refetch();
        handleDialogClose();
      } catch (error: any) {
        console.error("Error saving payment requisition:", error);
        // toast.error(error.data.error.message, {
        //   position: toast.POSITION.TOP_RIGHT,
        // });
      }
  };

  const handleReverseClick = async (paymentReq: PaymentRequisition) => {
    if (!paymentReq?.id) return;

    const confirmReverse = window.confirm(
      `Reverse payment request ${paymentReq.id}? This will cancel it in Procurement only and free the PO balance.`,
    );

    if (!confirmReverse) return;

    try {
      await updatePaymentReq({
        id: paymentReq.id,
        paymentRequisitionPartial: {
          id: paymentReq.id,
          status: 'Cancelled',
          lastActionedDate: new Date().toISOString(),
        },
      }).unwrap();

      toast.success('Payment request reversed in Procurement.');
      refetch();
    } catch (error: any) {
      console.error('Error reversing payment request:', error);
      toast.error(
        error?.data?.error?.message ||
        error?.error ||
        'Failed to reverse payment request.',
      );
    }
  };

  if (poLoading || prLoading) return <CircularProgress />;
  if (poError && !isFundsRequest) return <div>Error loading purchase orders</div>;
  if (prError) return <div>Error loading payment requests</div>;


  return (
    <Box paddingY={3}>
      <Typography variant="h5" gutterBottom color="secondary" sx={{ fontWeight: 'bold' }}>
        Payment Requests
      </Typography>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>

        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateClick}
            disabled={!(user?.roles?.includes('procurement-admin') || user?.roles?.includes('admin'))}
        >
          Create Payment Requests
        </Button>
      </Box>


      <TableContainer>
        <Table sx={{ backgroundColor: '#ffffff', borderRadius: 2, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
          <TableHead sx={{ backgroundColor: '#1976d2', color: 'white' }}>
            <TableRow>

              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Payment Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Payment Method</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Due Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date Created</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Prepared By</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activePaymentReqs?.length && activePaymentReqs?.length > 0 ? (
              activePaymentReqs.map((req) => (
                <TableRow key={req.id}>

                  <TableCell> {formatCurrency(req.amount, req.currency || 'usd')}</TableCell>
                  <TableCell>{req.paymentType}</TableCell>
                  <TableCell>{req.paymentMethod}</TableCell>
                  <TableCell>
                    {(() => {
                      switch (req.status) {
                        case 'Approved':
                          return <Chip label="Approved" color="primary" />; 
                        case 'Rejected':
                          return <Chip label="Rejected" color="error" />; 
                        case 'Paid':
                          return <Chip label="Paid" color="success" />; 
                        case 'Prioritized':
                          return <Chip label="Prioritized" sx={{ backgroundColor: '#ffb300', color: '#fff' }} />; 
                        case 'Scheduled':
                          return <Chip label="Scheduled" sx={{ backgroundColor: '#9c27b0', color: '#fff' }} />; 
                        case 'Awaiting Approval':
                        default:
                          return <Chip label="Awaiting Approval" color="warning" />; 
                      }
                    })()}
                  </TableCell>


                  <TableCell>{req.dueDate ? new Date(req.dueDate).toLocaleDateString() : 'N/A'}</TableCell>
                  <TableCell>{req.dateCreated ? new Date(req.dateCreated).toLocaleDateString() : 'N/A'}</TableCell>
                  <TableCell>
                    <RenderApproverName user={req.preparedBy} />
                  </TableCell>
                  <TableCell>
                    <Button onClick={() => handleEditClick(req)}>Edit</Button>
                    {String(req.status).toLowerCase() !== 'cancelled' && (
                      <Button
                        color="error"
                        onClick={() => handleReverseClick(req)}
                        sx={{ ml: 1 }}
                      >
                        Reverse
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6}>No payment requisition created as yet.</TableCell>
              </TableRow>
            )}
          </TableBody>

        </Table>
      </TableContainer>
      {(purchaseOrders?.[0]?.quotation?.grandTotal || (isFundsRequest && !purchaseOrders?.[0])) && (
        <Box sx={{ marginTop: 4, display: 'flex', justifyContent: 'center' }}>
          <Table sx={{ width: 'auto', minWidth: 400, border: '1px solid #ccc', borderRadius: 2 }}>
            <TableBody>
              <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                <TableCell>
                  <Typography variant="h6" fontWeight="bold">Total Requested</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="h6">
                    {formatCurrency(
                      totalRequested,
                      isFundsRequest
                        ? (formData.currency || 'usd')
                        : (purchaseOrders?.[0]?.quotation?.currency || 'usd')
                    )}
                  </Typography>
                </TableCell>
              </TableRow>

              <TableRow sx={{ backgroundColor: '#fce4ec' }}>
                <TableCell>
                  <Typography variant="h6" fontWeight="bold">
                    {isFundsRequest && !purchaseOrders?.[0] ? 'Budget Value' : 'PO Grand Total'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="h6">
                    {formatCurrency(
                      isFundsRequest && !purchaseOrders?.[0]
                        ? requisitionTotalCost
                        : (purchaseOrders?.[0]?.quotation?.grandTotal || 0),
                      isFundsRequest && !purchaseOrders?.[0]
                        ? (formData.currency || 'usd')
                        : (purchaseOrders?.[0]?.quotation?.currency || 'usd')
                    )}
                  </Typography>
                </TableCell>
              </TableRow>

              <TableRow
                sx={{
                  backgroundColor:
                    ((isFundsRequest && !purchaseOrders?.[0]
                      ? requisitionTotalCost
                      : (purchaseOrders?.[0]?.quotation?.grandTotal || 0)) - totalRequested) > 0
                      ? '#e8f5e9'
                      : ((isFundsRequest && !purchaseOrders?.[0]
                        ? requisitionTotalCost
                        : (purchaseOrders?.[0]?.quotation?.grandTotal || 0)) - totalRequested) === 0
                        ? '#fff8e1'
                        : '#ffebee',
                }}
              >
                <TableCell>
                  <Typography variant="h6" fontWeight="bold">Remaining Balance</Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="h6"
                    sx={{
                      color:
                        ((isFundsRequest && !purchaseOrders?.[0]
                          ? requisitionTotalCost
                          : (purchaseOrders?.[0]?.quotation?.grandTotal || 0)) - totalRequested) > 0
                          ? 'green'
                          : ((isFundsRequest && !purchaseOrders?.[0]
                            ? requisitionTotalCost
                            : (purchaseOrders?.[0]?.quotation?.grandTotal || 0)) - totalRequested) === 0
                            ? '#ff9800'
                            : 'red',
                      fontWeight: 'bold',
                    }}
                  >
                    {formatCurrency(
                      (isFundsRequest && !purchaseOrders?.[0]
                        ? requisitionTotalCost
                        : (purchaseOrders?.[0]?.quotation?.grandTotal || 0)) - totalRequested,
                      isFundsRequest && !purchaseOrders?.[0]
                        ? (formData.currency || 'usd')
                        : (purchaseOrders?.[0]?.quotation?.currency || 'usd')
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

      )}




      <Dialog open={isDialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentPaymentReq ? 'Edit Payment Requisition' : 'Create Payment Requisition'}
        </DialogTitle>
        <DialogContent>
          {(isFundsRequest || purchaseOrders?.[0]) && (
            <Card
              elevation={3}
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%)',
                borderLeft: '4px solid #1976d2',
                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 24px 0 rgba(0,0,0,0.15)'
                }
              }}
            >
              <Box display="flex" alignItems="center" mb={1}>
                <ReceiptLongIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#1976d2',
                    letterSpacing: '0.5px'
                  }}
                >
                  Payment Requisition Details
                </Typography>
              </Box>

              <Divider sx={{ mb: 2, borderColor: 'rgba(25, 118, 210, 0.2)' }} />

              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={1.5}>
                    <NumbersIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <Box component="span" fontWeight={600}>{isFundsRequest ? 'Requisition Number:' : 'PO Number:'}</Box>{' '}
                      {isFundsRequest
                        ? (procurementProcess?.requisition?.reqNumber || 'N/A')
                        : purchaseOrders?.[0]?.purchaseOrderNumber}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center" mb={1.5}>
                    <LocationOnIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <Box component="span" fontWeight={600}>Allocation:</Box>
                      <Box component="span" display="block" ml={2}>
                        {procurementProcess.requisition.development.name},
                      </Box>
                      <Box component="span" display="block" ml={2}>
                        {procurementProcess.requisition.project.name}
                      </Box>
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={1.5}>
                    <DescriptionIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <Box component="span" fontWeight={600}>Details:</Box> {procurementProcess.requisition.title}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center" mb={1.5}>
                    <CurrencyExchangeIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <Box component="span" fontWeight={600}>Currency:</Box>
                      <Box
                        component="span"
                        sx={{
                          bgcolor: '#e3f2fd',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          ml: 1
                        }}
                      >
                        {isFundsRequest
                          ? (formData.currency || 'USD')
                          : (purchaseOrders?.[0]?.quotation?.currency || 'USD')}
                      </Box>
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center">
                    <AttachMoneyIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <Box component="span" fontWeight={600}>{isFundsRequest ? 'Budget Value:' : 'PO Value:'}</Box>
                      <Box
                        component="span"
                        sx={{
                          fontSize: '1.1rem',
                          fontWeight: 700,
                          color: '#2e7d32',
                          ml: 1
                        }}
                      >
                        {isFundsRequest
                          ? formatCurrency(requisitionTotalCost, formData.currency || 'usd')
                          : formatCurrency(purchaseOrders?.[0]?.quotation?.grandTotal, purchaseOrders?.[0]?.quotation?.currency || 'usd')}

                      </Box>
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          )}

          {/* <TextField
            label="Reference Number"
            fullWidth
            margin="normal"
            value={formData.referenceNumber || ''}
            onChange={(e) => handleFormChange('referenceNumber', e.target.value)}
            required
          /> */}

          <Box display="flex" gap={2} mt={2}>
            <TextField
              select
              label="Payment Method"
              fullWidth
              value={formData.paymentMethod || ''}
              onChange={(e) => handleFormChange('paymentMethod', e.target.value)}
              required
            >
              <MenuItem value="Nostro">Nostro</MenuItem>
              <MenuItem value="Cash">Cash</MenuItem>
            </TextField>

            <TextField
              label="Amount"
              fullWidth
              type="number"
              value={formData.amount || 0}
              onChange={(e) => handleFormChange('amount', parseFloat(e.target.value))}
              inputProps={{ min: 0, step: 0.01 }}
              required
            />

            <TextField
              label="Rate"
              fullWidth
              type="number"
              value={formData.rate || 0}
              onChange={(e) => handleFormChange('rate', parseFloat(e.target.value))}
              inputProps={{ min: 0, step: 0.01 }}
              required
            />


            <TextField
              label="USD Equivalent"
              fullWidth
              value={
                formData.amount && formData.rate && formData.rate > 0
                  ? (formData.amount / formData.rate).toFixed(2)
                  : ''
              }
              InputProps={{
                readOnly: true,
              }}
            />


          </Box>

          <Box mt={2} sx={{ display: 'flex', gap: 2 }}>
            <TextField
              select
              label="Payment Type"
              fullWidth
              value={formData.paymentType || ''}
              onChange={(e) => handleFormChange('paymentType', e.target.value)}
              required
            >
              <MenuItem value="DEPOSIT">DEPOSIT</MenuItem>
              <MenuItem value="INTERIM">INTERIM</MenuItem>
              <MenuItem value="FINAL PAYMENT">FINAL PAYMENT</MenuItem>
              <MenuItem value="ONCE OFF">ONCE OFF</MenuItem>
            </TextField>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Due Date"
                value={formData.dueDate ? new Date(formData.dueDate) : null}
                onChange={(date: Date | null) => handleFormChange('dueDate', date)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
                minDate={new Date()}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Raised Date"
                value={formData.dateCreated ? new Date(formData.dateCreated) : null}
                onChange={(date: Date | null) => handleFormChange('dateCreated', date)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
                maxDate={new Date()}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!formData.paymentMethod || !hasValidAmount || !formData.dueDate || !formData.dateCreated || !formData.paymentType}
          >
            {currentPaymentReq ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentRequest;